import { useEffect, useState } from "react";
import { Plus, Trash2, X, Send } from "lucide-react";
import api, { formatErr } from "../lib/apiClient";
import { fmtCAD } from "../lib/format";
import EmployeeAccountsManager from "../components/EmployeeAccountsManager";

export default function Employees() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const [inviteResult, setInviteResult] = useState(null);

  const blank = { name: "", email: "", phone: "", role: "Technician", permission_role: "technician", hourly_rate: 0, active: true };

  const load = async () => { const { data } = await api.get("/employees"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = { ...editing, hourly_rate: Number(editing.hourly_rate || 0) };
      if (editing.id) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/employees/${id}`); await load(); };

  const invite = async (emp) => {
    try {
      const { data } = await api.post(`/employees/${emp.id}/invite`);
      setInviteResult({ ...data, name: emp.name });
    } catch (e) { alert(formatErr(e)); }
  };

  return (
    <div data-testid="employees-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Employees</h1><p className="text-sm text-slate-500 mt-1">{items.length} team member{items.length !== 1 ? "s" : ""}</p></div>
        <button onClick={() => { setEditing(blank); setShowForm(true); }} data-testid="add-employee-btn" className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> Add Employee</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Name</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Role</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Contact</th>
              <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Hourly Rate</th>
              <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No employees yet.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{e.name}</td>
                <td className="px-4 py-3 text-slate-600">{e.role}</td>
                <td className="px-4 py-3 text-slate-600">{e.email || "—"}<div className="text-xs text-slate-500">{e.phone}</div></td>
                <td className="px-4 py-3 text-right tabular">{fmtCAD(e.hourly_rate)}/hr</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${e.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{e.active ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 text-right"><button onClick={() => { setEditing(e); setShowForm(true); }} className="text-xs text-[#0F4C81] hover:underline mr-2">Edit</button><button onClick={() => del(e.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-md max-w-md w-full p-6 space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl font-bold">{editing.id ? "Edit" : "Add"} Employee</h3><button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button></div>
            {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">Name *</label><input data-testid="emp-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Email</label><input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Phone</label><input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Role</label><input value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Hourly Rate ($)</label><input data-testid="emp-rate" type="number" step="0.01" value={editing.hourly_rate} onChange={(e) => setEditing({ ...editing, hourly_rate: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md tabular" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Permission</label>
                <select value={editing.permission_role || "technician"} onChange={(e) => setEditing({ ...editing, permission_role: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                  <option value="technician">Technician (jobs/time only)</option>
                  <option value="office">Office (no financial reports)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>
              <div className="col-span-2"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label></div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
      {inviteResult && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-md max-w-md w-full p-6 space-y-3">
            <div className="flex justify-between"><h3 className="font-display text-xl font-bold">Invite sent to {inviteResult.name}</h3><button onClick={() => setInviteResult(null)} className="text-slate-400 hover:text-slate-700">✕</button></div>
            <p className="text-sm text-slate-600">Sent to <b>{inviteResult.email}</b>. If your Resend key isn't set, share this link manually:</p>
            <code className="block bg-slate-100 px-3 py-2 rounded text-xs break-all font-mono">{inviteResult.reset_link}</code>
            <p className="text-xs text-slate-500">Link expires in 24 hours.</p>
            <button onClick={() => setInviteResult(null)} className="w-full px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Close</button>
          </div>
        </div>
      )}

      <EmployeeAccountsManager/>
    </div>
  );
}
